import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { PageBackButton } from '@src/common-components/button';
import { ArrowLeftIcon2 } from '@src/components';
import { IFrameMessage, POST_MESSAGE_TYPE, Product } from '@src/constants';
import { StepWizardForm } from '@src/features';
import { useComponentsConfig, useResetFlatSteps } from '@src/hooks';
import {
  AuthActionTypes,
  PresetSettingActionTypes,
  Store,
  UserActionTypes,
  WizardActionTypes,
} from '@src/store';
import { ComponentsConfigType } from '@src/types';

import { useAnalyticsPets } from './hooks';

export const PetsForm: FC = () => {
  const { t } = useTranslation();
  const {
    state: {
      stateWizard: { currentStep },
      stateFormPets: { presetData },
      statePresetSetting: { isPresetSettingDisplayed },
      stateUser: { agentLogin },
    },
    dispatch,
  } = useContext(Store);
  const { components } = useComponentsConfig(
    Product.PETS,
    !!presetData ? ComponentsConfigType.MAIN : ComponentsConfigType.DEFAULT
  );
  const navigate = useNavigate();

  useAnalyticsPets();

  const goBack = () => {
    if (currentStep === 1) {
      dispatch({
        type: AuthActionTypes.SetConfirmationId,
        payload: undefined,
      });

      dispatch({
        type: AuthActionTypes.SetAuthenticationToken,
        payload: undefined,
      });

      dispatch({
        type: UserActionTypes.SetCachedPhoneNumber,
        payload: undefined,
      });

      dispatch({
        type: UserActionTypes.SetAuthenticationRefetchUid,
        payload: uuidv4(),
      });

      navigate('/login');
    } else {
      navigate(-1);
    }
  };
  const maxStep = components?.length || 0;

  useEffect(() => {
    if (isPresetSettingDisplayed) {
      dispatch({
        type: WizardActionTypes.SetCurrentStep,
        payload: maxStep,
      });
      dispatch({
        type: PresetSettingActionTypes.SetIsPresetSettingDisplayed,
        payload: false,
      });
    }
  }, []);

  const postGoBackMessageToDigitalAgent = useCallback(() => {
    window.top?.postMessage(
      JSON.stringify({
        message: IFrameMessage.TRIGGER_GO_BACK,
        type: POST_MESSAGE_TYPE,
      }),
      window.envUrls.AGENT_URL
    );
  }, []);

  return isPresetSettingDisplayed ? null : (
    <>
      {!!agentLogin?.length && currentStep === 1 && (
        <PageBackButton
          variant="text"
          icon={<ArrowLeftIcon2 />}
          onClick={postGoBackMessageToDigitalAgent}
        >
          {t('COMMON:buttons.back')}
        </PageBackButton>
      )}
      <StepWizardForm
        scrollToIframeOnChange
        maxStep={maxStep}
        useResetSteps={useResetFlatSteps}
        goBack={goBack}
      >
        {components?.map((Component, index) => {
          if (currentStep === index + 1) {
            return (
              <React.Fragment key={index}>
                <Component />
              </React.Fragment>
            );
          }
          return null;
        })}
      </StepWizardForm>
    </>
  );
};
